import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";

import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, PageHeader } from "@/components/Page";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

import { WikiAdminArticleEditor } from "@/components/wiki/admin/WikiAdminArticleEditor";
import { WikiAdminCategoryManager } from "@/components/wiki/admin/WikiAdminCategoryManager";
import { WikiAdminSettingsEditor } from "@/components/wiki/admin/WikiAdminSettingsEditor";

const emptyCategoryDraft = { title: "", slug: "", description: "", display_order: 0, published: true };
const emptyArticleDraft = { id: null, title: "", slug: "", excerpt: "", category_slug: "", subcategory: "", body_markdown: "", tags_text: "", search_terms_text: "", related_article_ids_text: "", display_order: 0, published: false };

const splitTextList = (value) => (value || "").split(/[\n,]+/).map((item) => item.trim()).filter(Boolean);
const articleToDraft = (article) => ({ ...emptyArticleDraft, ...article, tags_text: (article.tags || []).join(", "), search_terms_text: (article.search_terms || []).join(", "), related_article_ids_text: (article.related_article_ids || []).join("\n") });
const settingsToDraft = (settings) => ({ ...settings, featured_article_ids: settings.featured_article_ids || [], popular_article_ids: settings.popular_article_ids || [], quick_links: settings.quick_links || [], new_player_steps: settings.new_player_steps || [] });

export default function WikiAdmin() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [categoryDraft, setCategoryDraft] = useState(emptyCategoryDraft);
  const [articleDraft, setArticleDraft] = useState(emptyArticleDraft);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/wiki/dashboard");
      setCategories(data.categories || []);
      setArticles(data.articles || []);
      setSettingsDraft(settingsToDraft(data.settings || {}));
      if (!articleDraft.id && data.categories?.length) {
        setArticleDraft((prev) => ({ ...prev, category_slug: prev.category_slug || data.categories[0].slug }));
      }
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) return <Loader label="Opening wiki admin…" />;
  if (user && !user.is_admin) return <Navigate to="/" replace />;

  const saveCategory = async (category) => {
    setBusy(true);
    try {
      await api.patch(`/admin/wiki/categories/${category.id}`, { title: category.title, slug: category.slug, description: category.description, display_order: Number(category.display_order || 0), published: !!category.published });
      toast.success("Wiki section saved.");
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const createCategory = async () => {
    setBusy(true);
    try {
      await api.post("/admin/wiki/categories", { ...categoryDraft, display_order: Number(categoryDraft.display_order || 0) });
      toast.success("Wiki section created.");
      setCategoryDraft(emptyCategoryDraft);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const deleteCategory = async (category) => {
    if (!window.confirm(`Delete section “${category.title}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/wiki/categories/${category.id}`);
      toast.success("Wiki section deleted.");
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const saveArticle = async () => {
    setBusy(true);
    const payload = {
      title: articleDraft.title,
      slug: articleDraft.slug,
      excerpt: articleDraft.excerpt,
      category_slug: articleDraft.category_slug,
      subcategory: articleDraft.subcategory,
      body_markdown: articleDraft.body_markdown,
      tags: splitTextList(articleDraft.tags_text),
      search_terms: splitTextList(articleDraft.search_terms_text),
      related_article_ids: splitTextList(articleDraft.related_article_ids_text),
      display_order: Number(articleDraft.display_order || 0),
      published: !!articleDraft.published,
    };
    try {
      if (articleDraft.id) {
        await api.patch(`/admin/wiki/articles/${articleDraft.id}`, payload);
        toast.success("Wiki page saved.");
      } else {
        await api.post("/admin/wiki/articles", payload);
        toast.success("Wiki page created.");
      }
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const deleteArticle = async (article) => {
    if (!window.confirm(`Delete page “${article.title}”?`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/wiki/articles/${article.id}`);
      toast.success("Wiki page deleted.");
      setArticleDraft(emptyArticleDraft);
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  const saveSettings = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/wiki/settings", {
        hero_title: settingsDraft.hero_title,
        hero_subtitle: settingsDraft.hero_subtitle,
        featured_article_ids: Array.isArray(settingsDraft.featured_article_ids) ? settingsDraft.featured_article_ids : splitTextList(settingsDraft.featured_article_ids),
        popular_article_ids: Array.isArray(settingsDraft.popular_article_ids) ? settingsDraft.popular_article_ids : splitTextList(settingsDraft.popular_article_ids),
        quick_links: settingsDraft.quick_links,
        new_player_steps: settingsDraft.new_player_steps,
      });
      toast.success("Wiki homepage settings saved.");
      await load();
    } catch (error) {
      toast.error(apiErr(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 py-8 space-y-6">
      <PageHeader title="Wiki Admin" subtitle="Create sections, write pages, and control the wiki home." />
      <Tabs defaultValue="articles" className="space-y-4">
        <TabsList className="bg-zinc-950/80 border border-zinc-800 h-auto p-1 flex-wrap justify-start">
          <TabsTrigger data-testid="wiki-admin-tab-articles" value="articles">Pages</TabsTrigger>
          <TabsTrigger data-testid="wiki-admin-tab-categories" value="categories">Sections</TabsTrigger>
          <TabsTrigger data-testid="wiki-admin-tab-settings" value="settings">Homepage</TabsTrigger>
        </TabsList>

        <TabsContent value="articles">
          <WikiAdminArticleEditor
            articles={articles}
            categories={categories}
            draft={articleDraft}
            setDraft={setArticleDraft}
            onCreateNew={() => setArticleDraft({ ...emptyArticleDraft, category_slug: categories[0]?.slug || "" })}
            onSelectArticle={(article) => setArticleDraft(articleToDraft(article))}
            onSave={saveArticle}
            onDelete={deleteArticle}
            busy={busy}
          />
        </TabsContent>

        <TabsContent value="categories">
          <WikiAdminCategoryManager
            categories={categories}
            draft={categoryDraft}
            onDraftChange={(field, value) => setCategoryDraft((prev) => ({ ...prev, [field]: value }))}
            onCreate={createCategory}
            onChange={(id, field, value) => setCategories((prev) => prev.map((item) => item.id === id ? { ...item, [field]: value } : item))}
            onSave={saveCategory}
            onDelete={deleteCategory}
            busy={busy}
          />
        </TabsContent>

        <TabsContent value="settings">
          {settingsDraft && (
            <WikiAdminSettingsEditor
              settings={settingsDraft}
              articles={articles}
              onFieldChange={(field, value) => setSettingsDraft((prev) => ({ ...prev, [field]: field.endsWith("_ids") ? splitTextList(value) : value }))}
              onQuickLinkChange={(index, field, value) => setSettingsDraft((prev) => ({ ...prev, quick_links: prev.quick_links.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }))}
              onQuickLinkAdd={() => setSettingsDraft((prev) => ({ ...prev, quick_links: [...prev.quick_links, { id: crypto.randomUUID(), label: "", href: "" }] }))}
              onQuickLinkRemove={(index) => setSettingsDraft((prev) => ({ ...prev, quick_links: prev.quick_links.filter((_, itemIndex) => itemIndex !== index) }))}
              onPathStepChange={(index, field, value) => setSettingsDraft((prev) => ({ ...prev, new_player_steps: prev.new_player_steps.map((item, itemIndex) => itemIndex === index ? { ...item, [field]: value } : item) }))}
              onPathStepAdd={() => setSettingsDraft((prev) => ({ ...prev, new_player_steps: [...prev.new_player_steps, { id: crypto.randomUUID(), label: "", description: "", article_id: "", href: "" }] }))}
              onPathStepRemove={(index) => setSettingsDraft((prev) => ({ ...prev, new_player_steps: prev.new_player_steps.filter((_, itemIndex) => itemIndex !== index) }))}
              onSave={saveSettings}
              busy={busy}
            />
          )}
        </TabsContent>
      </Tabs>
    </main>
  );
}